import { useState,useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, X, Mail, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import WaitlistModal from "@/components/WaitlistModal";
import BetaWelcomeModal from "@/components/BetaWelcomeModal";
/* ─── Forgot Password Modal ─── */
function ForgotPasswordModal({ onClose, prefillEmail }) {
  const [step, setStep] = useState("input"); // "input" | "sent"
  const [resetEmail, setResetEmail] = useState(prefillEmail || "");
  const [busy, setBusy] = useState(false);



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resetEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      // P3 #11: real forgot-password call. Backend always returns 200
      // (no enumeration) so we always show the "check your inbox" state.
      await api.post("/auth/forgot-password", { email: resetEmail });
      setStep("sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,31,61,0.45)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl p-8 animate-[fadeUp_0.2s_ease]">
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#475569] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {step === "input" ? (
          <>
            {/* Icon */}
            <div className="h-11 w-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-5">
              <Mail className="h-5 w-5 text-[#0F1F3D]" />
            </div>

            <h3 className="font-display text-2xl font-bold text-[#0F1F3D]">Reset your password</h3>
            <p className="text-sm text-[#475569] mt-2">
              Enter the email linked to your account and we'll send you a reset link.
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="reset-email-input"
                  className="mt-2 w-full px-4 py-2.5 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit-button"
                className="w-full px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F] disabled:opacity-60 transition-colors"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2.5 rounded-md border border-[#E2E8F0] text-[#475569] text-sm font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                Cancel
              </button>
            </form>
          </>
        ) : (
          /* Success state */
          <div className="flex flex-col items-center text-center py-4">
            <div className="h-14 w-14 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
            </div>
            <h3 className="font-display text-2xl font-bold text-[#0F1F3D]">Check your inbox</h3>
            <p className="text-sm text-[#475569] mt-3 max-w-xs">
              We've sent a password reset link to{" "}
              <span className="font-semibold text-[#0F1F3D]">{resetEmail}</span>.
              It expires in 60 minutes.
            </p>
            <p className="text-xs text-[#94A3B8] mt-3">
              Didn't receive it? Check your spam folder or{" "}
              <button
                onClick={() => setStep("input")}
                className="text-[#0F1F3D] font-medium hover:underline"
              >
                try again
              </button>
              .
            </p>
            <button
              onClick={onClose}
              data-testid="reset-done-button"
              className="mt-6 px-6 py-2.5 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F] transition-colors"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>

      {/* Keyframe — defined inline so no external CSS file needed */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ─── Login Page ─── */
export default function Login() {
  const [showBetaModal, setShowBetaModal] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  // useEffect(() => {
  //   const timer = setTimeout(() => setShowBetaModal(true), 500);
  //   return () => clearTimeout(timer);
  // }, []);

  useEffect(() => {
    const token = localStorage.getItem("lr_token");

    if (token) {
      nav("/app", { replace: true });
    }
  }, [nav]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const [showWaitlist, setShowWaitlist] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      setShowBetaModal(true);
      //nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleBetaModalClose = () => {
    setShowBetaModal(false);
    nav("/app"); // navigate only after the user dismisses the modal
  };

  return (
    <>
      <div className="min-h-screen grid lg:grid-cols-2 bg-white">
        {/* ── Left panel ── */}
        <div className="hidden lg:flex relative overflow-hidden bg-[#0F1F3D] p-12 flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
             <img src='/logo.png' alt='Launch Radar' style={{
              filter: 'brightness(0) invert(1)',
             }} className="w-[200px]"/>
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Welcome back</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Daily AI-scored intelligence for teams tracking markets, tenders and regulation.
            </h1>
            <p className="mt-4 text-white/70 max-w-md">
              Create a project once. LaunchRadar monitors your sources every morning and delivers a ranked feed of what matters.
            </p>
          </div>
          <div className="text-xs text-white/50">© 2026 LaunchRadar</div>
        </div>

        {/* ── Right panel ── */}
        <div className="flex items-center justify-center p-8 lg:p-16">
          <form onSubmit={submit} className="w-full max-w-sm">
            <h2 className="font-display text-3xl font-bold text-[#0F1F3D]">Sign in</h2>
            <p className="text-sm text-[#475569] mt-2">Use your account or the seeded demo credentials.</p>

            <div className="mt-8 space-y-4">
              {/* Email */}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#64748B]">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="mt-2 w-full px-4 py-2.5 rounded-md border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#64748B]">Password</label>
                  <button
                    type="button"
                    onClick={() => setShowReset(true)}
                    data-testid="forgot-password-link"
                    className="text-xs text-[#0F1F3D] font-medium hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="mt-2 w-full px-4 py-2.5 pr-11 rounded-md border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="toggle-login-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-[#94A3B8] hover:text-[#475569] transition-colors"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="w-full px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F] disabled:opacity-60 transition-colors"
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </div>

            {/* <div className="mt-6 text-sm text-[#475569]">
              New to LaunchRadar?{" "}
              <Link to="/signup" data-testid="goto-signup-link" className="text-[#0F1F3D] font-medium hover:underline">
                Create an account
              </Link>
            </div> */}

            <div className="mt-6 text-sm text-[#475569]">
              <button
                type="button"
                onClick={() => setShowWaitlist(true)}
                data-testid="goto-signup-link"
                className="text-[#0F1F3D] font-medium underline hover:no-underline"
              >
                Join the waitlist
              </button>
            </div>
          </form>
        </div>
      </div>
            

      <BetaWelcomeModal open={showBetaModal} onClose={handleBetaModalClose} />
      {/* Forgot password modal */}
      {showReset && (
        <ForgotPasswordModal
          onClose={() => setShowReset(false)}
          prefillEmail={email}
        />
      )}

      {showWaitlist && (
        <WaitlistModal
          onClose={() => setShowWaitlist(false)}
        />
      )}
    </>
  );
}